import React, { useEffect, useState } from "react"
import { useNavigate, useParams, useLocation } from "react-router-dom"
import { getPlayers } from "../services/playerService"
import API from "../services/api"
import { getTeamLogo, getAssetURL } from "../configs/assets"
import { getUser } from '../utils/storage'
import { useToast } from "../components/GlobalToast"
import { parseMatchTime } from "../utils/dateUtils"

const CreateTeam = () => {

  const navigate = useNavigate()
  const { matchId } = useParams()
  const location = useLocation()
  const toast = useToast()

  const [players, setPlayers] = useState([])
  const [selected, setSelected] = useState([])
  const [captain, setCaptain] = useState(null)
  const [viceCaptain, setViceCaptain] = useState(null)
  const [activeTab, setActiveTab] = useState("WK")
  const [match, setMatch] = useState(null)
  const [showPreviewPopup, setShowPreviewPopup] = useState(false)
  const [step, setStep] = useState(1) // ✅ Back to 2-step process
  const [saving, setSaving] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [editTeamId, setEditTeamId] = useState(null)

  // Helper for grouping players for popup
  const groupPlayers = (teamPlayers) => ({
    WK: teamPlayers.filter(p => p.role === "WK"),
    BAT: teamPlayers.filter(p => p.role === "BAT"),
    AR: teamPlayers.filter(p => p.role === "AR"),
    BOWL: teamPlayers.filter(p => p.role === "BOWL"),
  })

  // Helper for rendering a player in popup/preview (Premium Style)
  const renderPlayer = (p, capId, vcId) => (
    <div key={p.player_id} className="flex flex-col items-center text-center m-0.5 w-14 group/player">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center p-0.5 relative transition-transform shadow-[0_4px_10px_rgba(0,0,0,0.5)] ${capId === p.player_id ? 'bg-yellow-500' : vcId === p.player_id ? 'bg-blue-500' : 'bg-white/90'}`}>
        <div className="w-full h-full rounded-full bg-theme-bg border border-theme-border overflow-hidden">
          <img
            src={getAssetURL(p.image) || "https://img.icons8.com/color/96/cricket-player.png"}
            alt={p.player_name}
            className="w-full h-full object-cover"
            onError={(e) => { e.target.src = "https://img.icons8.com/color/96/cricket-player.png" }}
          />
        </div>

        {/* C / VC Badge (Premium Glassy) */}
        {capId === p.player_id && (
          <span className="absolute -top-1 -right-1 bg-yellow-500 text-black text-[8px] w-4.5 h-4.5 rounded-full flex items-center justify-center border border-black font-black z-10 shadow-lg">C</span>
        )}
        {vcId === p.player_id && (
          <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-[8px] w-4.5 h-4.5 rounded-full flex items-center justify-center border border-black font-black z-10 shadow-lg">VC</span>
        )}
      </div>
      <p className="text-[8px] bg-theme-card text-theme-text rounded px-1.5 mt-1 truncate max-w-full font-black border border-theme-border shadow-sm leading-tight">
        {p.player_name.split(" ")[0]}
      </p>
    </div>
  )

  useEffect(() => {
    fetchPlayers()
    fetchMatch()

    // ✅ Handle Edit Mode from URL Query Params or location state
    const params = new URLSearchParams(window.location.search);
    const teamIdFromUrl = params.get("editTeamId");

    if (teamIdFromUrl || (location.state && location.state.editTeam)) {
      setIsEditMode(true);
      const tId = teamIdFromUrl || location.state.editTeam.team_id || location.state.editTeam.id;
      setEditTeamId(tId);
      
      if (location.state && location.state.editTeam) {
        loadTeamData(location.state.editTeam);
      } else if (teamIdFromUrl) {
        fetchAndLoadTeam(teamIdFromUrl);
      }
    }
  }, [matchId, location.state])

  const loadTeamData = (team) => {
    if (team.players) {
      setSelected(team.players.map(p => p.player_id));
      const cap = team.players.find(p => p.is_captain);
      const vcap = team.players.find(p => p.is_vice_captain);
      if (cap) setCaptain(cap.player_id);
      if (vcap) setViceCaptain(vcap.player_id);
    }
  };

  const fetchAndLoadTeam = async (tId) => {
    try {
      const res = await API.get(`/team/my-teams?matchId=${matchId}`);
      const team = (res.data.teams || []).find(t => String(t.team_id || t.id) === String(tId));
      if (team) {
        loadTeamData(team);
      }
    } catch (err) {
      console.error("Error loading team for edit:", err);
    }
  };

  // 🔴 AUTO-REDIRECT: If match starts while user is on this page, go to home
  useEffect(() => {
    const checkMatchStatus = () => {
      if (match && parseMatchTime(match) <= new Date()) {
        toast.info("⏰ Match has started! Redirecting to home page.");
        navigate("/home", { replace: true });
      }
    };
    const interval = setInterval(checkMatchStatus, 5000); // Check every 5 seconds
    return () => clearInterval(interval);
  }, [match, navigate])

  const fetchMatch = async () => {
    try {
      const res = await API.get(`/matches/${matchId}`)
      const matchData = res.data.data || res.data
      setMatch(Array.isArray(matchData) ? matchData[0] : matchData)
    } catch (err) {
      console.log(err)
    }
  }

  const fetchPlayers = async () => {
    try {
      const res = await getPlayers(String(matchId))

      // ✅ INITIAL SORT BY CREDITS
      const sorted = (res.data || []).sort((a, b) => Number(b.credit) - Number(a.credit))
      setPlayers(sorted)
    } catch (err) {
      console.log(err)
    }
  }

  // ✅ SORT PLAYERS BY STATUS WHEN LINEUP ANNOUNCED
  const sortedPlayers = React.useMemo(() => {
    if (!match?.lineup_announced) return players;
    return [...players].sort((a, b) => {
      if (a.is_playing && !b.is_playing) return -1;
      if (!a.is_playing && b.is_playing) return 1;
      return Number(b.credit) - Number(a.credit);
    });
  }, [players, match?.lineup_announced]);

  // ✅ TOTAL CREDITS CALCULATE
  const totalCredits = players
    .filter(p => selected.includes(p.player_id))
    .reduce((sum, p) => sum + Number(p.credit), 0)

  // ✅ TEAM COUNT
  const teamCount = selected.reduce((acc, id) => {
    const player = players.find(p => p.player_id === id)
    if (player) acc[player.team] = (acc[player.team] || 0) + 1
    return acc
  }, {})

  const togglePlayer = (id) => {
    const player = players.find(p => p.player_id === id)
    if (selected.includes(id)) {
      setSelected(selected.filter(p => p !== id))
    } else {
      if (selected.length >= 11) return toast.warn("Only 11 players allowed")
      if (totalCredits + Number(player.credit) > 100) return toast.warn("Not enough credits")
      if (teamCount[player.team] >= 7) return toast.warn("Max 7 players from one team")
      setSelected([...selected, id])
    }
  }

  const selectCaptain = (id) => {
    if (id === viceCaptain) return toast.warn("Captain & VC cannot be same")
    setCaptain(id)
  }

  const selectViceCaptain = (id) => {
    if (id === captain) return toast.warn("Captain & VC cannot be same")
    setViceCaptain(id)
  }

  const handleNext = () => {
    if (selected.length !== 11) return toast.warn("Select 11 players")

    // ✅ Role Validations
    const selectedPlayers = players.filter(p => selected.includes(p.player_id))
    const getCount = (r) => selectedPlayers.filter(p => {
      const role = (p.role || "").toLowerCase()
      if (r === 'WK') return role.includes("wk") || role.includes("wicket")
      if (r === 'BAT') return role.includes("bat")
      if (r === 'AR') return role.includes("all-round") || role.includes("ar")
      if (r === 'BOWL') return role.includes("bowl")
      return false
    }).length

    const wk = getCount('WK'), bat = getCount('BAT'), ar = getCount('AR'), bowl = getCount('BOWL')

    if (wk < 1 || wk > 4) return toast.warn("Select 1-4 Wicket Keeper (WK). Current: " + wk)
    if (bat < 3 || bat > 6) return toast.warn("Select 3-6 Batsmen (BAT). Current: " + bat)
    if (ar < 1 || ar > 4) return toast.warn("Select 1-4 All Rounders (AR). Current: " + ar)
    if (bowl < 3 || bowl > 6) return toast.warn("Select 3-6 Bowlers (BOWL). Current: " + bowl)

    setStep(2)
  }

  const createTeam = async () => {
    if (saving) return
    try {
      if (selected.length !== 11) return toast.warn("Select 11 players")
      if (!captain || !viceCaptain) return toast.warn("Select Captain & Vice Captain")

      setSaving(true)
      const user = getUser()
      if (isEditMode) {
        // ✅ UPDATE MODE
        await API.put(`/team/update/${editTeamId}`, {
          userId: user.id || 1,
          players: selected,
          captain,
          viceCaptain
        })
        toast.success("Team updated successfully! 🏏")
      } else {
        // ✅ CREATE MODE
        await API.post("/team/create", {
          userId: user.id || 1,
          matchId: matchId,
          players: selected,
          captain,
          viceCaptain
        })
        toast.success("Team saved successfully! 🏏")
      }

      navigate(`/match/${matchId}`, { replace: true, state: { activeTab: 'my-teams' } })
    } catch (err) {
      toast.error(err.response?.data?.error || "Error")
    } finally {
      setSaving(false)
    }
  }

  // ✅ STEP 1: SQUAD SELECTION UI
  if (step === 1) {
    return (
      <div className="bg-theme-bg min-h-screen text-theme-text pb-24 flex flex-col">
        {/* HEADER */}
        <div className="p-4 bg-theme-card border-b border-theme-border sticky top-0 z-30 shadow-theme-sm">
          <div className="flex justify-between items-center bg-theme-bg rounded-xl p-3 border border-theme-border mb-3">
            <div className="flex items-center gap-2 min-w-0">
              <img src={getTeamLogo(match?.team1, match?.team1_logo)} className="w-6 h-6 object-contain" alt="" />
              <span className="uppercase text-theme-text text-[10px] font-bold truncate">{match?.team1_short || match?.team1?.substring(0, 3)}</span>
            </div>
            <div className="flex flex-col items-center mx-2">
              <span className="text-theme-text-sec text-[8px] font-bold uppercase tracking-widest">VS</span>
            </div>
            <div className="flex items-center gap-2 min-w-0 justify-end">
              <span className="uppercase text-theme-text text-[10px] font-bold truncate">{match?.team2_short || match?.team2?.substring(0, 3)}</span>
              <img src={getTeamLogo(match?.team2, match?.team2_logo)} className="w-6 h-6 object-contain" alt="" />
            </div>
          </div>
          <div className="bg-theme-bg rounded-full h-1.5 w-full overflow-hidden flex border border-theme-border">
            <div className="bg-accent h-full transition-all duration-300" style={{ width: `${(selected.length / 11) * 100}%` }}></div>
          </div>
          <div className="flex justify-between text-[10px] mt-2 text-theme-text-sec px-1 font-bold uppercase tracking-tight">
            <span>{selected.length}/11 Players</span>
            <span>{100 - totalCredits} Cr Left</span>
          </div>
        </div>

        {/* TABS */}
        <div className="flex justify-around bg-theme-card border-b border-theme-border sticky top-[108px] z-30">
          {["WK", "BAT", "AR", "BOWL"].map(tab => {
            const count = players.filter(p => {
              const r = p.role?.toLowerCase() || "";
              if (tab === "WK") return r.includes("wk") || r.includes("wicket");
              if (tab === "BAT") return r.includes("bat");
              if (tab === "AR") return r.includes("all-round") || r.includes("ar");
              if (tab === "BOWL") return r.includes("bowl");
              return false;
            }).length;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-3 text-[10px] font-bold uppercase tracking-widest transition-all relative ${activeTab === tab ? "text-accent" : "text-theme-text-sec opacity-60"}`}
              >
                {tab} <span>({count})</span>
                {activeTab === tab && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent"></div>}
              </button>
            )
          })}
        </div>

        {/* 🏟️ TEAM HEADERS & SELECTION GRID */}
        {(() => {
          const allTeamsFound = [...new Set(players.map(p => p.team))].filter(Boolean);
          const t1HeaderShorthand = match?.team1?.toLowerCase().substring(0, 3) || "";

          const t1Val = allTeamsFound.find(t =>
            t.toLowerCase().includes(t1HeaderShorthand) ||
            t1HeaderShorthand.includes(t.toLowerCase().substring(0, 3))
          ) || allTeamsFound[0];

          const t2Val = allTeamsFound.find(t => t !== t1Val) || allTeamsFound[1];

          const currentTabPlayers = sortedPlayers.filter(p => {
            const r = p.role?.toLowerCase() || "";
            if (activeTab === "WK") return r.includes("wk") || r.includes("wicket");
            if (activeTab === "BAT") return r.includes("bat");
            if (activeTab === "AR") return r.includes("all-round") || r.includes("ar");
            if (activeTab === "BOWL") return r.includes("bowl");
            return false;
          });

          const pOnLeft = currentTabPlayers.filter(p => p.team === t1Val);
          const pOnRight = t2Val ? currentTabPlayers.filter(p => p.team === t2Val) : [];

          const renderPlayerItem = (p, isLeft) => (
            <div key={p.player_id} className={`flex flex-col p-3 border-b border-theme-border relative min-h-[85px] transition-all active:bg-theme-bg-sec ${isLeft ? '' : 'text-right'} ${selected.includes(p.player_id) ? "bg-accent/5" : ""}`}>
              <div className={`flex items-start gap-2 ${isLeft ? '' : 'justify-end flex-row-reverse'}`}>
                <div className="relative shrink-0">
                  <img src={getAssetURL(p.image) || "https://img.icons8.com/color/96/cricket-player.png"} className="w-10 h-10 rounded-full border border-theme-border object-cover bg-theme-bg shadow-sm" alt="" onError={(e)=>e.target.src="https://img.icons8.com/color/96/cricket-player.png"} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold truncate text-theme-text uppercase tracking-tight">{p.player_name}</p>
                  <p className="text-[8px] text-theme-text-sec font-bold uppercase truncate">{p.team} • {p.role}</p>
                  <div className={`flex items-center gap-2 mt-1 ${isLeft ? '' : 'justify-end'}`}>
                    <p className="text-[9px] font-bold text-theme-text">{Number(p.credit).toFixed(0.5)} <span className="text-[7px] text-theme-text-sec uppercase">Cr</span></p>
                    <p className="text-[9px] font-bold text-accent">{p.points || 0} <span className="text-[7px] text-theme-text-sec uppercase">Pts</span></p>
                  </div>
                </div>
                <button onClick={() => togglePlayer(p.player_id)} className={`w-7 h-7 rounded-lg flex items-center justify-center font-bold text-sm shrink-0 transition-all active:scale-90 ${selected.includes(p.player_id) ? "bg-red-500 text-white" : "bg-accent text-white"}`}>{selected.includes(p.player_id) ? "✕" : "+"}</button>
              </div>
              {match?.lineup_announced && (
                <div className={`mt-1.5 flex ${isLeft ? 'justify-start' : 'justify-end'}`}>
                  <span className={`text-[7px] font-bold w-fit px-2 py-0.5 rounded uppercase tracking-wider border ${p.is_playing ? "text-green-600 bg-green-500/10 border-green-500/20" : "text-red-500 bg-red-500/10 border-red-500/20"}`}>
                    {p.is_playing ? "Playing" : "Out"}
                  </span>
                </div>
              )}
            </div>
          );

          const renderPlayerList = (playerList, isLeft) => {
            const playing = playerList.filter(p => p.is_playing);
            const notPlaying = playerList.filter(p => !p.is_playing);

            return (
              <div className="flex flex-col">
                {match?.lineup_announced && playing.length > 0 && (
                  <div className={`p-2 bg-green-500/10 border-b border-theme-border flex items-center gap-2 ${isLeft ? '' : 'justify-end'}`}>
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                    <span className="text-[8px] font-black text-green-500 uppercase tracking-[0.2em]">Playing</span>
                  </div>
                )}
                {playing.map(p => renderPlayerItem(p, isLeft))}

                {match?.lineup_announced && notPlaying.length > 0 && (
                  <div className={`p-2 bg-red-500/10 border-b border-theme-border flex items-center gap-2 ${isLeft ? '' : 'justify-end'}`}>
                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                    <span className="text-[8px] font-black text-red-500 uppercase tracking-[0.2em]">Not Playing</span>
                  </div>
                )}
                {notPlaying.map(p => renderPlayerItem(p, isLeft))}

                {playerList.length === 0 && (
                  <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-theme-bg/10">
                    <p className="text-gray-700 text-[10px] font-black uppercase tracking-[0.2em] opacity-30 italic">No {activeTab}s</p>
                    <p className="text-gray-800 text-[8px] font-bold uppercase mt-1">{isLeft ? (t1Val || "Team 1") : (t2Val || "Team 2")}</p>
                  </div>
                )}
              </div>
            );
          };

          return (
            <>
              {/* Sticky Team Headers */}
              <div className="grid grid-cols-2 bg-theme-bg border-b border-theme-border py-3 sticky top-[150px] z-20 px-4 shadow-theme-sm">
                <div className="flex flex-col items-start px-2 gap-0.5 overflow-hidden">
                  <span className="text-[8px] font-bold uppercase text-theme-text-sec tracking-widest">Team 1</span>
                  <span className="text-[10px] font-bold uppercase text-theme-text truncate max-w-full">{t1Val || match?.team1}</span>
                </div>
                <div className="flex flex-col items-end px-2 gap-0.5 overflow-hidden text-right">
                  <span className="text-[8px] font-bold uppercase text-theme-text-sec tracking-widest">Team 2</span>
                  <span className="text-[10px] font-bold uppercase text-theme-text truncate max-w-full">{t2Val || match?.team2}</span>
                </div>
              </div>

              {/* Selection Grid */}
              <div className="grid grid-cols-2 relative bg-theme-bg flex-1 min-h-[500px]">
                <div className="absolute left-1/2 top-0 bottom-0 w-px bg-theme-border z-0"></div>
                <div className="border-r border-theme-border">
                  {renderPlayerList(pOnLeft, true)}
                </div>
                <div>
                  {renderPlayerList(pOnRight, false)}
                </div>
              </div>
            </>
          );
        })()}

        <div className="fixed bottom-0 left-0 right-0 p-4 bg-theme-card border-t border-theme-border flex gap-4 shadow-theme-lg z-40">
          <button onClick={() => setShowPreviewPopup(true)} className="flex-1 bg-theme-bg text-theme-text font-bold py-3.5 rounded-xl text-[10px] uppercase tracking-wider border border-theme-border transition-all active:scale-95">Preview</button>
          <button onClick={handleNext} disabled={selected.length !== 11} className={`flex-1 font-bold py-3.5 rounded-xl text-[10px] uppercase tracking-wider transition-all active:scale-95 ${selected.length === 11 ? "bg-accent text-white shadow-sm" : "bg-theme-bg-sec text-theme-text-sec opacity-50 cursor-not-allowed border border-theme-border"}`} >Continue</button>
        </div>

        {/* PREVIEW */}
        {showPreviewPopup && (
          <div className="fixed inset-0 z-50 flex flex-col justify-end bg-theme-bg/80 backdrop-blur-sm p-0 animate-fade-in" onClick={() => setShowPreviewPopup(false)}>
            <div className="bg-theme-card w-full max-w-md rounded-t-[32px] shadow-theme-lg overflow-hidden animate-slide-up h-[85vh] flex flex-col border-t-4 border-accent" onClick={e => e.stopPropagation()}>
              <div className="p-4 bg-theme-card border-b border-theme-border flex justify-between items-center text-theme-text shrink-0">
                <h3 className="font-bold text-xs uppercase tracking-widest">Ground Preview</h3>
                <button onClick={() => setShowPreviewPopup(false)} className="text-theme-text-sec hover:text-theme-text p-2 rounded-full active:bg-theme-bg transition-all">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                </button>
              </div>
              <div className="p-2 pb-6 flex-1 relative flex flex-col justify-evenly bg-[#1a4d2e] overflow-hidden">
                {/* 🏏 PREMIUM CSS PITCH BACKGROUND */}
                <div className="absolute inset-0 bg-gradient-to-b from-[#064e3b] via-[#1a4d2e] to-[#064e3b] opacity-100">
                  <div className="absolute inset-0 shadow-[inset_0_0_100px_rgba(0,0,0,0.5)]"></div>
                  {/* Pitch Lines */}
                  <div className="absolute left-1/2 top-10 bottom-10 w-px bg-white/20 -translate-x-1/2"></div>
                  <div className="absolute top-1/2 left-10 right-10 h-px bg-white/20 -translate-y-1/2"></div>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full border border-white/20"></div>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white/10"></div>
                </div>

                <div className="relative z-10 h-full">
                  {(() => {
                    const teamPlayers = players.filter(p => selected.includes(p.player_id))
                    const grouped = groupPlayers(teamPlayers)
                    return (
                      <div className="h-full flex flex-col justify-between py-6">
                        <div className="flex justify-center flex-wrap gap-4">{grouped.WK.map(p => renderPlayer(p, captain, viceCaptain))}</div>
                        <div className="flex justify-center flex-wrap gap-4">{grouped.BAT.map(p => renderPlayer(p, captain, viceCaptain))}</div>
                        <div className="flex justify-center flex-wrap gap-4">{grouped.AR.map(p => renderPlayer(p, captain, viceCaptain))}</div>
                        <div className="flex justify-center flex-wrap gap-4">{grouped.BOWL.map(p => renderPlayer(p, captain, viceCaptain))}</div>
                      </div>
                    )
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // ✅ STEP 2: CAPTAIN SELECTION
  const sList = players.filter(p => selected.includes(p.player_id))

  return (
    <div className="bg-theme-bg min-h-screen text-theme-text pb-24 flex flex-col">
      <div className="p-4 bg-theme-card border-b border-theme-border flex items-center sticky top-0 z-30 shadow-theme-sm">
        <button onClick={() => setStep(1)} className="w-10 h-10 flex items-center justify-center bg-theme-bg rounded-xl border border-theme-border text-theme-text transition-all active:scale-95 mr-3 shadow-theme-sm">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        </button>
        <h1 className="text-sm font-bold uppercase tracking-tight text-theme-text">Choose Captains</h1>
      </div>

      <div className="bg-theme-card p-5 flex justify-around items-center border-b border-theme-border">
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-full border flex items-center justify-center font-bold text-xs ${captain ? 'bg-yellow-500 border-yellow-400 text-black shadow-sm' : 'bg-theme-bg border-theme-border text-theme-text-sec'}`}>C</div>
          <div><p className="text-[10px] font-bold uppercase text-theme-text-sec tracking-wide">Captain</p><p className="text-xs font-bold text-theme-text">{captain ? players.find(p => p.player_id === captain)?.player_name.split(' ')[0] : "--"}</p></div>
        </div>
        <div className="h-10 w-px bg-theme-border"></div>
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-full border flex items-center justify-center font-bold text-xs ${viceCaptain ? 'bg-accent border-accent text-white shadow-sm' : 'bg-theme-bg border-theme-border text-theme-text-sec'}`}>VC</div>
          <div><p className="text-[10px] font-bold uppercase text-theme-text-sec tracking-wide">Vice Captain</p><p className="text-xs font-bold text-theme-text">{viceCaptain ? players.find(p => p.player_id === viceCaptain)?.player_name.split(' ')[0] : "--"}</p></div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-12 p-3 bg-theme-bg-sec text-[10px] font-bold uppercase text-theme-text-sec tracking-wider border-b border-theme-border px-4 sticky top-0 z-20 backdrop-blur-md bg-theme-bg-sec/90">
          <div className="col-span-8">Players Selection</div>
          <div className="col-span-2 text-center">C</div>
          <div className="col-span-2 text-center">VC</div>
        </div>
        {sList.map(p => (
          <div key={p.player_id} className={`grid grid-cols-12 items-center p-3 border-b border-theme-border px-4 transition-all ${captain === p.player_id || viceCaptain === p.player_id ? 'bg-accent/5' : ''}`}>
            <div className="col-span-8 flex items-center gap-3">
              <img src={getAssetURL(p.image) || "https://img.icons8.com/color/96/cricket-player.png"} className="w-9 h-9 rounded-full border border-theme-border object-cover bg-theme-bg" alt="" onError={(e)=>e.target.src="https://img.icons8.com/color/96/cricket-player.png"} />
              <div className="min-w-0">
                <p className="text-[11px] font-bold text-theme-text truncate">{p.player_name}</p>
                <p className="text-[9px] text-theme-text-sec font-bold uppercase">{p.role}</p>
              </div>
            </div>
            <div className="col-span-2 flex justify-center">
              <button onClick={() => selectCaptain(p.player_id)} className={`w-9 h-9 rounded-full border-2 font-bold text-[10px] transition-all ${captain === p.player_id ? 'bg-yellow-500 border-yellow-400 text-black shadow-sm' : 'bg-theme-bg border-theme-border text-theme-text-sec active:scale-90'}`}>C</button>
            </div>
            <div className="col-span-2 flex justify-center">
              <button onClick={() => selectViceCaptain(p.player_id)} className={`w-9 h-9 rounded-full border-2 font-bold text-[10px] transition-all ${viceCaptain === p.player_id ? 'bg-accent border-accent text-white shadow-sm' : 'bg-theme-bg border-theme-border text-theme-text-sec active:scale-90'}`}>VC</button>
            </div>
          </div>
        ))}
      </div>

      <div className="fixed bottom-0 left-0 right-0 p-4 bg-theme-card border-t border-theme-border shadow-theme-lg z-40">
        <button
          onClick={createTeam}
          disabled={saving || !captain || !viceCaptain}
          className={`w-full font-bold py-4 rounded-xl text-xs uppercase tracking-widest transition-all shadow-sm flex items-center justify-center gap-2 ${(saving || !captain || !viceCaptain) ? 'bg-theme-bg-sec text-theme-text-sec opacity-50 cursor-not-allowed border border-theme-border' : 'bg-accent text-white hover:brightness-105 active:scale-95'}`}
        >
          {saving && (
            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          )}
          {saving ? (isEditMode ? 'Updating...' : 'Saving...') : (isEditMode ? 'Update Team' : 'Create Team')}
        </button>
      </div>
    </div>
  )
}

export default CreateTeam